import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';

// The key still says "consent" because the old Accept/Reject banner wrote it.
// Keeping it means visitors who answered that banner are not shown this again.
const NOTICE_SEEN_KEY = 'socialyse-cookie-consent';

/**
 * Cookie notice, shown at the bottom of the screen until the visitor presses OK.
 *
 * It is a notice, not a choice. The site only stores what it needs to work —
 * sign-in, resuming an upload, the questionnaire draft, dashboard layout — and
 * none of that needs consent. The old banner offered Accept and Reject, but
 * nothing ever read the answer, so both buttons did the same thing (#218).
 *
 * Before adding analytics, or any script or request to another company, this
 * has to become a real choice that the new code checks before it runs.
 */
export function CookieNotice() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(localStorage.getItem(NOTICE_SEEN_KEY) === null);
  }, []);

  // While the notice is visible, reserve space at the bottom of the page so
  // it never overlaps content (e.g. the bottom card on /get-started).
  useEffect(() => {
    if (!isVisible) return;
    const prev = document.body.style.paddingBottom;
    document.body.style.paddingBottom = '6rem';
    return () => {
      document.body.style.paddingBottom = prev;
    };
  }, [isVisible]);

  const handleOk = () => {
    localStorage.setItem(NOTICE_SEEN_KEY, JSON.stringify({ seenAt: new Date().toISOString() }));
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-white/80"
      role="region"
      aria-label="Cookie notice"
    >
      <div className="container mx-auto flex max-w-6xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="flex-1 text-sm text-slate-700">
          <p className="font-semibold text-slate-900">Cookie notice</p>
          <p className="text-xs sm:text-sm">
            We only use the cookies and browser storage this site needs to work, such as keeping you signed in.
            We don't use any tracking, analytics or advertising cookies.
          </p>
        </div>
        <Button onClick={handleOk} size="sm" className="self-end sm:self-auto">
          OK
        </Button>
      </div>
    </div>
  );
}
